import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { usePolling } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * L'indicatore «Dobermann attivo» nella barra in alto: un led rosso
 * lampeggiante da antifurto, la faccia stilizzata del cane col collare
 * chiodato, e la scritta. Compare **solo quando la guardia è in servizio** —
 * attiva E con almeno una subnet — perché un antifurto acceso su zero
 * sorveglianza rassicurerebbe sul nulla.
 *
 * Sta nel layout, quindi si rilegge da solo di tanto in tanto: se qualcuno
 * accende la guardia da un'altra scheda, la barra lo mostra senza ricaricare.
 */
export default function DobermannBadge() {
  const { data } = usePolling(() => api.dobermann(), 60000)
  const inServizio = Boolean(data?.settings.enabled && (data?.watches.length ?? 0) > 0)
  if (!inServizio) return null

  const nuovi = data?.counts['nuovo'] ?? 0

  return (
    <Link
      to="/dobermann"
      className="dob-topbar"
      title={
        nuovi > 0
          ? t('{n} nuovi avvistamenti', { n: nuovi })
          : t('Dobermann in servizio')
      }
    >
      <span className="dob-topbar__led" aria-hidden="true" />
      <FacciaDobermann />
      <span className="dob-topbar__testo">{t('Dobermann attivo')}</span>
      {nuovi > 0 && <span className="dob-topbar__conta">{nuovi}</span>}
    </Link>
  )
}

/**
 * La faccia del Dobermann, stilizzata: due orecchie a punta, il muso che si
 * assottiglia, e un collare con le borchie a punta. Monocromatica
 * (`currentColor`) per stare sulla barra scura come sul chiaro. Geometrica di
 * proposito — un'icona, non un disegno.
 */
function FacciaDobermann() {
  return (
    <svg
      className="dob-topbar__faccia"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      {/* Orecchie a punta, dritte (Dobermann con orecchie tagliate). */}
      <path d="M7 8 L5.2 2.3 L9.2 6.2 Z" fill="currentColor" stroke="none" />
      <path d="M17 8 L18.8 2.3 L14.8 6.2 Z" fill="currentColor" stroke="none" />
      {/* Testa: fronte larga, guance, muso che scende a cuneo. */}
      <path d="M7 6.5 C6 8.5 6 10.5 7.2 12 L10.5 16.2 C11.3 17.1 12.7 17.1 13.5 16.2 L16.8 12 C18 10.5 18 8.5 17 6.5" />
      {/* Occhi a mandorla, inclinati: lo sguardo severo. */}
      <path d="M9 9 l1.4 0.7" />
      <path d="M15 9 l-1.4 0.7" />
      {/* Tartufo. */}
      <path d="M12 13 l-0.8 0.8 h1.6 Z" fill="currentColor" stroke="none" />
      {/* Il collare: una banda staccata dal muso, con le borchie a punta
          rivolte in giù — chiodato, non un sorriso. */}
      <path d="M7.6 18.3 h8.8" strokeWidth="2.2" />
      <path d="M9 19 l0.55 1.5 l0.55 -1.5 Z" fill="currentColor" stroke="none" />
      <path d="M11.45 19 l0.55 1.6 l0.55 -1.6 Z" fill="currentColor" stroke="none" />
      <path d="M13.9 19 l0.55 1.5 l0.55 -1.5 Z" fill="currentColor" stroke="none" />
    </svg>
  )
}
